import { Suspense, lazy } from 'react'
import { Link, router } from '@inertiajs/react'
import Logo from '../../components/Logo'
import TypeBadge from '../../components/TypeBadge'

// Lazy-load MemoryGraph so React Flow ships only when this page is visited.
// This satisfies REQ-7 (lazy-load) and DECISION-6.
const MemoryGraph = lazy(() => import('../../components/MemoryGraph'))

/**
 * Inertia page: Memories/Graph
 *
 * Props from MemoriesController#graph:
 *   memory    — { id, title, memory_type }
 *   nodes     — GraphNode[]  (raw traverse result)
 *   truncated — boolean
 *   count     — integer
 */
export default function Graph({ memory, nodes, truncated, count }) {
  function handleNodeClick(id) {
    router.visit(`/memories/${id}/graph`)
  }

  return (
    <div className="min-h-screen bg-zinc-900 text-zinc-100">
      {/* Header */}
      <header className="border-b border-zinc-700 px-6 py-3 flex items-center justify-between shrink-0 bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-800/30">
        <div className="flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2.5 no-underline group">
            <Logo variant="mark" size={28} />
            <span className="text-base font-mono font-bold tracking-[0.12em] uppercase text-zinc-100 group-hover:text-emerald-300 transition-colors">
              anamnesis
            </span>
          </Link>
          <a
            href={`/memories/${memory.id}`}
            onClick={e => { e.preventDefault(); router.visit(`/memories/${memory.id}`) }}
            className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-400 rounded-md px-2.5 py-1 bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
          >
            <span className="text-zinc-300">‹</span> detail
          </a>
        </div>
        <div className="flex items-center gap-2">
          <TypeBadge type={memory.memory_type} />
          <span className="text-xs font-mono text-zinc-400">{memory.title}</span>
        </div>
      </header>

      <div className="px-6 py-4">
        {/* Page title */}
        <div className="mb-4">
          <h1 className="text-sm font-mono font-bold text-zinc-100 mb-1">
            Memory Graph
          </h1>
          <p className="text-xs font-mono text-zinc-500">
            Relation graph for: <span className="text-zinc-300">{memory.title}</span>
          </p>
        </div>

        {/* Truncated banner (REQ-2) */}
        {truncated && (
          <div
            data-testid="truncated-banner"
            className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-800/60 bg-amber-950/30 text-xs font-mono text-amber-400"
          >
            <span>Showing partial graph.</span>
            <span className="text-amber-300/70">
              {count} nodes total — only the first 100 are displayed.
            </span>
          </div>
        )}

        {/* Graph canvas or empty state (REQ-5) */}
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-64 border border-zinc-700 rounded-lg bg-zinc-800/30">
              <p className="text-xs font-mono text-zinc-500">Loading graph...</p>
            </div>
          }
        >
          <MemoryGraph
            seedId={memory.id}
            nodes={nodes}
            onNodeClick={handleNodeClick}
          />
        </Suspense>
      </div>
    </div>
  )
}
